import type { toast } from '@Shelly/shelly-design-system/index';
import { user_data, user_settings } from 'src/stores/account/user';
import { app, os } from 'src/stores/session/native-bridge';
import type { CloudApiResponse } from 'src/types/responses/CloudApiResponse';
import { get } from 'svelte/store';
import { postReq } from '../http';
import { biometry_info_store, biometry_store } from 'src/stores/features/biometry';
import {
	BiometryStatus,
	type Biometry,
	type BiometryAuthResponse,
	type BiometryNativeResponseModel,
} from 'src/types/biometry/BiometryNativeResponseModel';
import { showBiometricAuthAndroid } from '../native_container_requests_android';
import { NativeOperationSystem } from '../init/types/init-type';
import { showBiometricAuthIos } from '../native_container_requests_ios';

export class BiometryService {
	/**
	 * Checks if biometric authentication can be called on device.
	 * @returns boolean - whether biometric authentication can be called
	 */
	static canCallBiometricsAuth(): boolean {
		const isApp = get(app);

		return (
			isApp && BiometryService.isBiometryAvailable() && BiometryService.isBiometryTurnedOnByUser()
		);
	}

	/**
	 * Checks if biometric authentication is turned on by the user in app settings.
	 * @returns boolean
	 */
	static isBiometryTurnedOnByUser(): boolean {
		const userSettings = get(user_settings);
		const biometricsValue = Number(userSettings.biometrics?.enabled);

		if (Number.isNaN(biometricsValue)) {
			return false;
		}

		return Boolean(biometricsValue);
	}

	/**
	 * Checks if biometric authentication is available on the device.
	 * @returns boolean
	 */
	static isBiometryAvailable(): boolean {
		const biometryMobileInfo = get(biometry_info_store);

		if (biometryMobileInfo?.error) {
			return false;
		}

		if (
			!Array.isArray(biometryMobileInfo?.biometry_status) ||
			biometryMobileInfo.biometry_status.length === 0
		) {
			return false;
		}

		if (
			biometryMobileInfo.biometry_status?.length >= 1 &&
			!biometryMobileInfo.biometry_status.includes(BiometryStatus.Enabled)
		) {
			return false;
		}

		return true;
	}

	/**
	 * Toggle the value of the biometrics setting
	 * Leaving it as 1 will prompt for biometrics first where pin is used
	 * @param enabled number - 1 or 0
	 * @returns Promise\<CloudApiResponse\>
	 */
	static async toggleBiometrySetting(enabled: boolean): Promise<CloudApiResponse> {
		try {
			let intValue = 0;

			if (enabled) {
				intValue = 1;
			}

			const res = await postReq('/user/set_biometrics', { enable: intValue });

			return res;
		} catch (error) {
			return { isok: false, error: error.message };
		}
	}

	/**
	 * Start the native biometric authentication process
	 * @param title - The title for the biometric prompt (Android only)
	 * @param subtitle - The title for the biometric prompt (Android only)
	 * @param description - The description for the biometric prompt
	 * @param negativeButton - The text for the cancel button
	 * @param iosFallbackButton - The text for the fallback button on iOS. If left empty, no fallback button will be shown.
	 * @returns
	 */
	static async startBiometricAuthentication(
		title: string,
		subtitle: string,
		description: string,
		negativeButton: string,
		iosFallbackButton = '',
	): Promise<BiometryAuthResponse> {
		return new Promise(async (resolve, reject) => {
			const platform = get(os);
			biometry_store.reset();

			if (platform === NativeOperationSystem.IOS) {
				await showBiometricAuthIos(iosFallbackButton, description, negativeButton);
			} else {
				await showBiometricAuthAndroid(title, subtitle, description, negativeButton);
			}

			const $biometryDataUnsub = biometry_store.subscribe((data) => {
				if (!data || data.error === 'Failed') {
					return;
				}

				if (data.error) {
					$biometryDataUnsub();
					return resolve({
						success: false,
						error: data?.error || 'Biometric authentication failed',
					});
				}

				$biometryDataUnsub();
				resolve({ success: true, biometry_data: data });
			});
		});
	}
}
